import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import apiClient from '../api/client';

const axiosInstance = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const retryApiCall = async (apiCall, maxRetries = 3, initialDelay = 1000) => {
  let lastError = null;
  let delay = initialDelay;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries) {
        const currentDelay = delay;
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        delay *= 1.5;
      }
    }
  }

  throw lastError;
};

const DaoProposals = () => {
  const [governanceVotes, setGovernanceVotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [proposalTitles, setProposalTitles] = useState({});
  const [titlesLoading, setTitlesLoading] = useState(true);

  const getEtherscanLink = (address) =>
    `https://etherscan.io/address/${address}`;

  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const fetchGovernanceVotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await retryApiCall(() =>
        apiClient.get('/api/crvlol/gov_proposals')
      );
      setGovernanceVotes(response.data.data);
    } catch (err) {
      setError('Failed to fetch governance votes');
      console.error('Error fetching governance votes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProposalTitles = useCallback(async () => {
    setTitlesLoading(true);
    try {
      const pages = [1, 2, 3];
      const results = await Promise.all(
        pages.map((page) =>
          retryApiCall(() =>
            axiosInstance.get(
              `https://prices.curve.finance/v1/dao/proposals?pagination=25&page=${page}&status_filter=all&type_filter=all`
            )
          ).catch(() => null)
        )
      );

      const titlesMap = {};
      results.forEach((response) => {
        if (response?.data?.proposals) {
          response.data.proposals.forEach((proposal) => {
            if (proposal.vote_id && proposal.metadata) {
              titlesMap[String(proposal.vote_id)] = proposal.metadata;
            }
          });
        }
      });
      setProposalTitles(titlesMap);
    } catch (err) {
      console.error('Error fetching proposal titles:', err);
    } finally {
      setTitlesLoading(false);
    }
  }, []);

  const truncateTitle = (title, maxLength = 80) => {
    if (!title || title.length <= maxLength) return title;
    return title.slice(0, maxLength).trim() + '…';
  };

  const gaugeStatusLabel = (status) => {
    if (status === 'unsupported') return 'Gauge analysis unsupported';
    return null;
  };

  useEffect(() => {
    fetchGovernanceVotes();
  }, [fetchGovernanceVotes]);

  useEffect(() => {
    fetchProposalTitles();
  }, [fetchProposalTitles]);

  return (
    <div className="dao-panel">
      <div className="governance-votes-section">
        <h2>Active Proposals</h2>
        {loading ? (
          <div className="loading">Loading active proposals...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : (
          <ul className="governance-votes-list">
            {governanceVotes.map((vote) => (
              <li key={vote.id} className="governance-vote-item">
                <div className="proposal-header">
                  <a
                    href={`https://www.curve.finance/dao/ethereum/proposals/${vote.id}-ownership`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="governance-vote-link"
                  >
                    #{vote.id}
                  </a>
                  <span className="proposal-title">
                    {titlesLoading ? (
                      <span className="title-skeleton" />
                    ) : proposalTitles[String(vote.id)] ? (
                      truncateTitle(proposalTitles[String(vote.id)])
                    ) : null}
                  </span>
                </div>
                {vote.gaugeValidations.length > 0 ? (
                  <div className="gauges-section">
                    {vote.gaugeValidations.map((validation, index) => (
                      <span
                        key={`${validation.gauge}-${index}`}
                        className="gauge-validation-item"
                      >
                        {validation.valid ? (
                          <span className="valid-emoji">✅</span>
                        ) : (
                          <span className="invalid-emoji">❌</span>
                        )}
                        <a
                          href={getEtherscanLink(validation.gauge)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gauge-link"
                        >
                          {formatAddress(validation.gauge)}
                        </a>
                      </span>
                    ))}
                  </div>
                ) : gaugeStatusLabel(vote.gaugeValidationStatus) ? (
                  <div className="proposal-gauge-status">
                    {gaugeStatusLabel(vote.gaugeValidationStatus)}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="dao-description">
        This page shows all active Curve governance proposals. If a proposal
        contains actions to add gauge(s) to the gauge controller, those
        addresses are validated to ensure they&apos;ve been deployed by a
        trusted factory. Data is fetched on chain from my{' '}
        <a
          href="https://etherscan.io/address/0x999901076BB47Ae96d135C567610270d006B8684#code"
          target="_blank"
          rel="noopener noreferrer"
        >
          gauge validator
        </a>{' '}
        system. The stable validator discovers every open ownership proposal and
        checks each parsed gauge against trusted Curve factory lookup paths.
      </div>
    </div>
  );
};

export default DaoProposals;
